import { useState, useEffect } from "react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { TrendingUp, TrendingDown, AlertCircle, DollarSign, Loader2, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { authFetch } from "../context/AuthContext";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const getListingUrls = (brand, itemName) => {
  const searchQuery = encodeURIComponent(`${brand} ${itemName}`);
  return {
    grailed: `https://www.grailed.com/sell`,
    depop: `https://www.depop.com/sell/`,
    poshmark: `https://poshmark.com/create-listing`,
  };
};

export function Insights() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [frequency, setFrequency] = useState("monthly");

  useEffect(() => {
    fetchInsights();
  }, [frequency]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/api/insights?frequency=${frequency}`);
      if (response.ok) {
        const data = await response.json();
        setInsights(data);
      } else {
        throw new Error("Failed to fetch insights");
      }
    } catch (error) {
      console.error("Failed to fetch insights:", error);
      toast.error("Failed to load insights");
    } finally {
      setLoading(false);
    }
  };

  const frequencyLabels = {
    weekly: "This Week",
    monthly: "This Month",
    seasonal: "This Season",
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Generating insights...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="insights-page">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight text-primary mb-2">
              Insights
            </h1>
            <p className="text-muted-foreground">
              {insights?.period_start} to {insights?.period_end}
            </p>
          </div>
          <div className="flex gap-2">
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger className="w-40 rounded-sm" data-testid="frequency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="seasonal">Seasonal</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={fetchInsights}
              className="rounded-sm"
              data-testid="refresh-insights-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border border-border shadow-none">
            <CardContent className="p-6">
              <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">
                Total Items
              </p>
              <p className="font-heading text-2xl font-medium text-primary">
                {insights?.total_items || 0}
              </p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-none">
            <CardContent className="p-6">
              <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">
                Days Logged
              </p>
              <p className="font-heading text-2xl font-medium text-primary">
                {insights?.total_logs || 0}
              </p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-none">
            <CardContent className="p-6">
              <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">
                Most Worn
              </p>
              <p className="font-heading text-2xl font-medium text-primary">
                {insights?.most_worn_items?.length || 0}
              </p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-none">
            <CardContent className="p-6">
              <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">
                Forgotten
              </p>
              <p className="font-heading text-2xl font-medium text-terracotta-500">
                {insights?.forgotten_items?.length || 0}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* AI Summary */}
        {insights?.ai_summary && (
          <Card className="border border-border shadow-none">
            <CardHeader className="p-6 pb-2">
              <CardTitle className="font-heading text-xl font-medium flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-terracotta-500" />
                AI Insights - {frequencyLabels[frequency]}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              <div className="space-y-6">
                {(() => {
                  // Parse the AI summary into sections
                  const sections = [];
                  let currentSection = null;
                  
                  insights.ai_summary.split('\n').forEach((line) => {
                    const trimmed = line.trim();
                    if (!trimmed) return;
                    
                    // Check for section heading (## Heading or **Heading**)
                    const headingMatch = trimmed.match(/^##\s*(.+)$/) || trimmed.match(/^\*\*([^*]+)\*\*$/);
                    if (headingMatch) {
                      if (currentSection) sections.push(currentSection);
                      currentSection = { heading: headingMatch[1].trim(), bullets: [] };
                      return;
                    }
                    
                    // Check for bullet point (• or - or *)
                    const bulletMatch = trimmed.match(/^[•\-\*]\s*(.+)$/);
                    if (bulletMatch && currentSection) {
                      // Clean up any remaining markdown
                      let bulletText = bulletMatch[1]
                        .replace(/\*\*/g, '')
                        .replace(/\*/g, '')
                        .trim();
                      currentSection.bullets.push(bulletText);
                      return;
                    }
                    
                    // If it's a standalone line that looks like a heading (short, no punctuation end)
                    if (trimmed.length < 50 && !trimmed.endsWith('.') && !trimmed.endsWith('!') && !currentSection) {
                      currentSection = { heading: trimmed.replace(/\*\*/g, ''), bullets: [] };
                      return;
                    }
                    
                    // Otherwise treat as bullet content if we have a section
                    if (currentSection && trimmed.length > 0) {
                      let cleanText = trimmed.replace(/\*\*/g, '').replace(/^\d+\.\s*/, '');
                      if (cleanText) currentSection.bullets.push(cleanText);
                    }
                  });
                  
                  if (currentSection) sections.push(currentSection);
                  
                  // If no sections were parsed, show raw content with basic formatting
                  if (sections.length === 0) {
                    return (
                      <p className="text-muted-foreground leading-relaxed">
                        {insights.ai_summary}
                      </p>
                    );
                  }
                  
                  return sections.map((section) => (
                    <div key={section.heading} className="space-y-2">
                      <h4 className="font-bold text-[1.1rem] text-[#722F37] tracking-tight">
                        {section.heading}
                      </h4>
                      <ul className="space-y-1.5 pl-1">
                        {section.bullets.map((bullet) => (
                          <li key={bullet.substring(0, 40)} className="flex items-start gap-2 text-muted-foreground">
                            <span className="text-[#722F37] mt-1.5 text-xs">•</span>
                            <span className="leading-relaxed">{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ));
                })()}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Most Worn Items */}
          <Card className="border border-border shadow-none">
            <CardHeader className="p-6 pb-2">
              <CardTitle className="font-heading text-xl font-medium flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                Most Worn
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Your wardrobe MVPs this {frequency === "weekly" ? "week" : frequency === "monthly" ? "month" : "season"}
              </p>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              {insights?.most_worn_items?.length > 0 ? (
                <div className="space-y-3">
                  {insights.most_worn_items.slice(0, 5).map((item, idx) => (
                    <div
                      key={item.item_id}
                      className="flex items-center justify-between p-3 bg-sand-50 rounded-sm"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-medium">
                          {idx + 1}
                        </span>
                        <div>
                          <p className="font-medium text-sm">{item.brand} {item.item_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.period_wears} wears • ${item.cost_per_wear}/wear
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No items worn 3+ times this period
                </p>
              )}
            </CardContent>
          </Card>

          {/* Least Worn Items */}
          <Card className="border border-border shadow-none">
            <CardHeader className="p-6 pb-2">
              <CardTitle className="font-heading text-xl font-medium flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-amber-600" />
                Least Worn
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Consider wearing these more often
              </p>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              {insights?.least_worn_items?.length > 0 ? (
                <div className="space-y-3">
                  {insights.least_worn_items.slice(0, 5).map((item) => (
                    <div
                      key={item.item_id}
                      className="flex items-center justify-between p-3 bg-sand-50 rounded-sm"
                    >
                      <div>
                        <p className="font-medium text-sm">{item.brand} {item.item_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.period_wears} wears • ${item.cost_per_wear}/wear
                        </p>
                      </div>
                      <span className="text-xs text-amber-600 font-medium">
                        ${item.total_cost}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  All items worn regularly!
                </p>
              )}
            </CardContent>
          </Card>

          {/* Forgotten Items */}
          <Card className="border border-border shadow-none">
            <CardHeader className="p-6 pb-2">
              <CardTitle className="font-heading text-xl font-medium flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                Forgotten Items
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Never worn - rediscover these pieces
              </p>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              {insights?.forgotten_items?.length > 0 ? (
                <div className="space-y-3">
                  {insights.forgotten_items.slice(0, 5).map((item) => (
                    <div
                      key={item.item_id}
                      className="flex items-center justify-between p-3 bg-red-50 rounded-sm"
                    >
                      <div>
                        <p className="font-medium text-sm">{item.brand} {item.item_name}</p>
                        <p className="text-xs text-muted-foreground">
                          Purchased for ${item.total_cost}
                        </p>
                      </div>
                      <span className="text-xs text-red-600 font-medium">
                        Never worn
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  Great job! All items have been worn.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Sell Suggestions */}
          <Card className="border border-border shadow-none">
            <CardHeader className="p-6 pb-2">
              <CardTitle className="font-heading text-xl font-medium flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-terracotta-500" />
                Consider Selling
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Items that might be better off in a new home
              </p>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              {insights?.sell_suggestions?.length > 0 ? (
                <div className="space-y-3">
                  {insights.sell_suggestions.map((item) => {
                    const listingUrls = getListingUrls(item.brand, item.item_name);
                    return (
                      <div
                        key={item.item_id}
                        className="p-3 bg-terracotta-500/5 rounded-sm"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-medium text-sm">{item.brand} {item.item_name}</p>
                            <p className="text-xs text-muted-foreground">
                              Paid ${item.total_cost}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-terracotta-500">
                              approx. ${item.estimated_value}
                            </p>
                            <p className="text-xs text-muted-foreground">Est. value</p>
                          </div>
                        </div>
                        {item.price_range && (
                          <div className="pt-2 border-t border-border/50 mb-2">
                            <p className="text-xs text-muted-foreground">
                              Price range: ${item.price_range.min} - ${item.price_range.max}
                              {item.listings_found > 0 && (
                                <span className="ml-2">({item.listings_found} listings found)</span>
                              )}
                            </p>
                          </div>
                        )}
                        {/* Quick List Buttons */}
                        <div className="flex gap-2 mt-2">
                          <a
                            href={listingUrls.grailed}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1"
                          >
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full text-xs h-8"
                              data-testid={`list-grailed-${item.item_id}`}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Grailed
                            </Button>
                          </a>
                          <a
                            href={listingUrls.depop}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1"
                          >
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full text-xs h-8"
                              data-testid={`list-depop-${item.item_id}`}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Depop
                            </Button>
                          </a>
                          <a
                            href={listingUrls.poshmark}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1"
                          >
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full text-xs h-8"
                              data-testid={`list-poshmark-${item.item_id}`}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Poshmark
                            </Button>
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No sell suggestions at this time
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
